/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import School from '@linc-technologies/linced-common/models/school';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr, hasMany, type AsyncHasMany } from '@warp-drive/legacy/model';
import Feature from '@linc-technologies/linced-common/enums/feature';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type FieldOptionsService from '@linc-technologies/linced-common/services/field-options';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import { mapBy, sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import type Person from 'linced-app/models/person';

const LOWEST_YEAR = 'hero:schoolType:lowestYear';
const HIGHEST_YEAR = 'hero:schoolType:highestYear';

function range(from: number, to: number) {
  const range = [];
  for (let i = from; i <= to; i++) {
    range.push(i);
  }
  return range;
}

// Proto: schools.School.
export default class _School extends School {
  // @ts-expect-error the app model has its own name.
  declare [Type]: 'schools/school';

  @service
  declare options: OptionsService;

  @service
  declare fieldOptions: FieldOptionsService;

  @attr('string', { defaultValue: '' })
  declare externalId: string;

  @hasMany('person', { async: true, inverse: null })
  // @ts-expect-error the app model makes the common attr a relationship.
  declare supportPersonIds: AsyncHasMany<Person>;

  @alias('supportPersonIds')
  declare staffWithSupportAccess: AsyncHasMany<Person>;

  // schoolType is an int that represents a NZ MoE school type:
  // - 20: Full Primary
  // - 21: Contributing Primary
  // - 23: Special
  // - 32: Composite
  // - 34: Correspondence
  // A school option value is unknown: the options service does not type it.
  @optionAlias('nzMOE:schoolType:type')
  schoolType: unknown;

  // yearLevels is used in reports and filters
  // convert to strings to assist filtering
  @computed(
    'jurisdiction',
    'moeYearLevels.[]',
    'yearLevelMap.[]',
    'customYearLevels.[]',
  )
  get yearLevels() {
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      return this.moeYearLevels!.map(String);
    }
    // Non-NZ MoE schools
    if (!isEmpty(this.yearLevelMap)) {
      // They have selected their own year levels
      return this.yearLevelMap;
    }
    // By default, use the lowest and highest years from the school's options
    return this.customYearLevels.map(String);
  }

  // yearLevelMap represents school's mapped year levels and labels, if they defined
  // them in fieldOptions. good example of use is NSW schools
  @computed('fieldOptions.yearLevelOptions.[]')
  get yearLevelMap() {
    if (!isEmpty(this.fieldOptions.yearLevelOptions)) {
      return mapBy(
        sortBy(
          // TS-STANDIN(field-options): common field-options.js is JS, so
          // yearLevelOptions infers as any.
          (
            this.fieldOptions.yearLevelOptions as {
              options: Iterable<{ label: string }>;
            }[]
          )[0]!.options,
          'valueInt',
        ),
        'label',
      );
    }
  }

  // customerYearLevels represents a range of years, calculated from the school's
  // defined lowestYear and highestYear.
  // primarily used by Non-NZ MoE independent schools.
  // It reads the fields of this school, not of the session school due to admin
  // and parent apps which work cross-tenant.
  @computed(`fields.{${LOWEST_YEAR},${HIGHEST_YEAR}}.value`, 'options.loaded')
  get customYearLevels() {
    const lowest = this.field(LOWEST_YEAR);
    const highest = this.field(HIGHEST_YEAR);
    // A school option value is unknown: the options service does not type it.
    const minYear: unknown = lowest
      ? lowest.value
      : this.options.getDefault(LOWEST_YEAR);
    const maxYear: unknown = highest
      ? highest.value
      : this.options.getDefault(HIGHEST_YEAR);
    if (typeof minYear !== 'number' || typeof maxYear !== 'number') {
      return [];
    }
    // Year 0 (new entrants) is valid. But the API does not send 0, so option
    // defaults of 0 to 0 are "not set" when the school sets no years.
    if (!lowest && !highest && minYear === 0 && maxYear === 0) {
      return [];
    }
    return range(minYear, maxYear);
  }

  // nzYearLevels represents standard school levels in NZ
  @computed('fields.nzMOE:schoolType:type.value')
  get nzYearLevels() {
    let from = 1;
    let to = 15;
    // TS-STANDIN(school): common school.js is JS, so fields values infer as
    // unknown.
    switch (
      parseInt(this.get('fields.nzMOE:schoolType:type.value') as string, 10)
    ) {
      case 20: // Full Primary
        from = 1;
        to = 8;
        break;
      case 21: // Contributing Primary
        from = 1;
        to = 6;
        break;
      case 22: // Intermediate
        from = 7;
        to = 8;
        break;
      case 23: // Special
        from = 1;
        to = 15;
        break;
      case 30: // Secondary (7-15)
        from = 7;
        to = 15;
        break;
      case 32: // Composite
        from = 1;
        to = 15;
        break;
      case 34: // Correspondence
        from = 1;
        to = 15;
        break;
      case 35: // Restricted composite
        from = 7;
        to = 10;
        break;
      case 40: // Secondary (9-15)
      case 41: // Teen Parent Unit
        from = 9;
        to = 15;
        break;
    }

    return range(from, to);
  }

  @computed(
    'nzYearLevels.[]',
    'jurisdiction',
    'fields.nzMOE:schoolType:type.value',
  )
  get moeYearLevels() {
    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      return;
    }
    const years: (number | string)[] = this.nzYearLevels.slice();
    // TS-STANDIN(school): common school.js is JS, so fields values infer as
    // unknown.
    switch (
      parseInt(this.get('fields.nzMOE:schoolType:type.value') as string, 10)
    ) {
      case 20: // Full Primary
      case 21: // Contributing Primary
      case 23: // Special
      case 32: // Composite
      case 34: // Correspondence
        years.unshift('0');
        break;
    }

    return years.map(String);
  }

  // financeEnabled calculates whether financing is enabled and whether caregivers have access to the Finance page
  @computed(
    'featuresEnabled.[]',
    'fields.finance:settings:caregiverAccessEnabled.value',
  )
  get financeEnabled() {
    if (!this.featureEnabled(Feature.PUPIL_BILLING)) {
      return false;
    }

    // TODO: Check to see whether the caregiver has access to any accounts in this school.
    // For now, just coerce to a bool in case it's null/undefined.
    return !!this.options.schoolGet(
      this,
      'finance:settings:caregiverAccessEnabled',
    );
  }

  @computed('jurisdiction')
  get jurisdictionalIdLabel() {
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return 'NSN';
      case Jurisdiction.AU_NSW:
        return 'SRN';
      default:
        return 'Jurisdictional ID';
    }
  }

  @computed('jurisdiction')
  get ethnicityLabel() {
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        return 'ATSI';
      default:
        return 'Ethnicity';
    }
  }

  @optionAlias('hero:features:comet')
  cometEnabled: unknown;
}
